/**
 * Session 58 — settingsSync
 *
 * Keeps the business settings a staff device USES for receipts (VAT rate,
 * service fee …) in step with what the owner saved under Menu Settings,
 * and reports any drift to the system error log.
 *
 * Background: a venue had VAT switched off + service fee on, yet a waiter's
 * receipt showed VAT 15%. The staff screen had never managed to load
 * `/auth/me` (its only source of business settings) and silently fell back
 * to the hard-coded 15% default, while the service fee came from the table
 * payload and therefore looked right. Nothing in the error log said a word.
 *
 * This module gives every dashboard:
 *   • a last-known-good cache of the business settings (localStorage), so a
 *     device never starts from "defaults";
 *   • a normaliser that mirrors exactly what the receipt code falls back
 *     to, so we can compare apples to apples;
 *   • a drift detector + reporter (`kind: "config"` → admin error log,
 *     category "Settings sync"), de-duplicated per device.
 */
import { reportClientError, crumb } from "@/lib/crashReporter";

const CACHE_PREFIX = "nfcs.business_settings:";
const REPORT_GAP_MS = 10 * 60 * 1000; // same drift reported at most every 10 min
const STALE_AFTER_MS = 60 * 1000;     // settings older than this count as stale

/** Fields every staff device must agree on (mirrors POS_SETTINGS_FIELDS). */
export const POS_SETTINGS_FIELDS = [
  "vat_rate",
  "service_fee_enabled",
  "service_fee_pct",
  "service_fee_separate",
  "long_wait_minutes",
  "kitchen_keep_until_delivered",
  "bar_keep_until_delivered",
  "require_staff_confirmation",
];

/** Fields that change what a guest is charged — drift here is an error. */
const RECEIPT_FIELDS = ["vat_rate", "service_fee_enabled", "service_fee_pct", "service_fee_separate"];

function _key(slugOrId) {
  return CACHE_PREFIX + String(slugOrId || "default").toLowerCase();
}

/** Last business settings this device successfully received. */
export function loadCachedBusiness(slugOrId) {
  try {
    const raw = window.localStorage.getItem(_key(slugOrId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function saveCachedBusiness(slugOrId, business) {
  if (!business || typeof business !== "object") return;
  try {
    window.localStorage.setItem(_key(slugOrId), JSON.stringify({ ...business, _cached_at: new Date().toISOString() }));
  } catch (_) { /* quota / private mode — best effort */ }
}

/**
 * What the receipt code ACTUALLY uses for a given business object —
 * including the defaults it falls back to when a field is missing.
 * `null` business → every value is a default.
 */
export function effectiveSettings(business) {
  const b = business || {};
  const num = (v, d) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? d : Number(v));
  return {
    vat_rate: num(b.vat_rate, 15),
    service_fee_enabled: b.service_fee_enabled == null ? null : Boolean(b.service_fee_enabled),
    service_fee_pct: num(b.service_fee_pct, 10),
    service_fee_separate: Boolean(b.service_fee_separate),
    long_wait_minutes: num(b.long_wait_minutes, 15),
    kitchen_keep_until_delivered: Boolean(b.kitchen_keep_until_delivered),
    bar_keep_until_delivered: Boolean(b.bar_keep_until_delivered),
    require_staff_confirmation: Boolean(b.require_staff_confirmation),
  };
}

/** Field-level diff between what the device used and what the server has. */
export function diffSettings(deviceBusiness, serverSettings, fields = POS_SETTINGS_FIELDS) {
  const device = effectiveSettings(deviceBusiness);
  const server = effectiveSettings(serverSettings);
  const out = [];
  for (const f of fields) {
    const dv = device[f];
    const sv = server[f];
    // A device that doesn't know whether the fee is on (null) is not
    // "wrong" per se — it defers to the table payload. Only flag a real
    // contradiction.
    if (f === "service_fee_enabled" && dv === null) continue;
    if (dv !== sv) out.push({ field: f, device: dv, server: sv });
  }
  return out;
}

/** True when `serverSettings` carries a receipt-relevant value the device lacks or disagrees with. */
export function hasReceiptDrift(deviceBusiness, serverSettings) {
  return diffSettings(deviceBusiness, serverSettings, RECEIPT_FIELDS).length > 0;
}

/** Merge the compact server settings into an existing business object. */
export function mergeBusinessSettings(business, serverSettings) {
  if (!serverSettings || typeof serverSettings !== "object") return business || null;
  const next = { ...(business || {}) };
  for (const f of POS_SETTINGS_FIELDS) {
    if (serverSettings[f] !== undefined) next[f] = serverSettings[f];
  }
  if (serverSettings.settings_updated_at) next.settings_updated_at = serverSettings.settings_updated_at;
  return next;
}

const _lastReportAt = new Map(); // dedupe key -> ts

/**
 * Report settings drift to the error log (category "Settings sync").
 *
 *   drift_kind: "settings_drift"   — device was charging with values that
 *                                     differ from Menu Settings
 *               "settings_defaults" — device had NO settings and used
 *                                     built-in defaults on a live screen
 *               "settings_refresh_failed" — /auth/me keeps failing while
 *                                     online, so settings cannot refresh
 */
export function reportSettingsDrift({ driftKind = "settings_drift", role = "staff", diff = [], staleForMs = null, source = "", business = null, extra = {} } = {}) {
  try {
    const fields = diff.map((d) => d.field).sort().join(",");
    const key = `${driftKind}|${role}|${fields}`;
    const now = Date.now();
    const last = _lastReportAt.get(key) || 0;
    if (now - last < REPORT_GAP_MS) return false;
    _lastReportAt.set(key, now);
    const human = diff.length
      ? diff.map((d) => `${d.field}: device=${JSON.stringify(d.device)} → server=${JSON.stringify(d.server)}`).join("; ")
      : "";
    const title = driftKind === "settings_refresh_failed"
      ? `${role} screen cannot refresh Menu Settings (${extra.failures || "?"} consecutive failures)`
      : driftKind === "settings_defaults"
        ? `${role} screen used built-in default settings instead of Menu Settings (${human})`
        : `${role} screen settings out of sync with Menu Settings (${human})`;
    crumb(`settings-drift ${driftKind} ${fields}`);
    reportClientError("config", { message: title }, {
      details: {
        drift_kind: driftKind,
        dashboard: role,
        fields: diff,
        stale_for_seconds: staleForMs == null ? null : Math.round(staleForMs / 1000),
        source,
        business_slug: business && business.slug ? business.slug : undefined,
        ...extra,
      },
    });
    return true;
  } catch (_) {
    return false;
  }
}

export { STALE_AFTER_MS };
